import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { SITE } from '../data/site';
import { useSession } from '../utils/session';
import { useTheme } from '../utils/useTheme';
import { ThemeToggle } from './ThemeToggle';
import { Icon } from './Icon';
import './Header.css';

const NAV = [
    { to: '/', label: 'Video', icon: 'video' },
    { to: '/photo', label: 'Photo', icon: 'image' },
];

export function Logo() {
    return (
        <Link to="/" className="logo" aria-label={`${SITE.name} home`}>
            <span className="logoMark"><Icon name="play" size={16} /></span>
            <span className="logoWord">
                {SITE.nameParts[0]}<span className="gradText">{SITE.nameParts[1]}</span>
            </span>
        </Link>
    );
}

// Active subscriber → credits balance; cancelled → manage; anyone else → upgrade.
function CreditsPill({ user, subStatus, onClick }) {
    if (subStatus === 'active') {
        return (
            <button type="button" className="creditsPill" onClick={onClick} title="Your credits">
                <span className="dot dot--success" />
                <span className="creditsPillNum">{user.credits ?? 0}</span>
                <span className="creditsPillUnit">credits</span>
            </button>
        );
    }
    if (subStatus === 'cancelled') {
        return (
            <button type="button" className="creditsPill creditsPill--cancelled creditsPill--wide" onClick={onClick}>
                <span className="dot dot--warning" />
                Cancelled
            </button>
        );
    }
    return (
        <button type="button" className="creditsPill creditsPill--upgrade creditsPill--wide" onClick={onClick}>
            <Icon name="star" size={13} />
            Upgrade
        </button>
    );
}

function AccountMenu({ user }) {
    const { subStatus, openAccount, openBilling, signOut } = useSession();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const pick = (fn) => () => { setOpen(false); fn(); };

    return (
        <div className="acctMenu" ref={ref}>
            <button
                type="button"
                className="acctAvatar"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Account menu"
            >
                {(user.email || '?').slice(0, 1).toUpperCase()}
            </button>
            {open && (
                <div className="acctDropdown glass" role="menu">
                    <div className="acctDropdownEmail">{user.email}</div>
                    {/* Phones have no room for the Upgrade pill in the bar, so the way to the plans lives here */}
                    {subStatus !== 'active' && (
                        <button type="button" role="menuitem" className="acctItem acctItem--upgrade" onClick={pick(openBilling)}>
                            <Icon name="star" size={15} /> {subStatus === 'cancelled' ? 'Renew plan' : 'Upgrade'}
                        </button>
                    )}
                    <button type="button" role="menuitem" className="acctItem" onClick={pick(openAccount)}>
                        <Icon name="user" size={16} /> My account
                    </button>
                    <button type="button" role="menuitem" className="acctItem" onClick={pick(signOut)}>
                        <Icon name="logout" size={16} /> Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

export function Header() {
    const { user, subStatus, authPending, openLogin, openBilling } = useSession();
    const { theme, toggle } = useTheme();
    const { pathname } = useLocation();
    const activeIdx = NAV.findIndex((n) => n.to === pathname);

    return (
        <header className="hdr">
            <div className="hdrBar glass">
                <Logo />

                <nav className="segmented hdrNav" data-active={activeIdx} aria-label="Main">
                    <span className="segmentedThumb" aria-hidden="true" />
                    {NAV.map((n) => (
                        <NavLink key={n.to} to={n.to} end className="segmentedItem">
                            <Icon name={n.icon} size={16} />
                            <span>{n.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="hdrRight">
                    <ThemeToggle theme={theme} onToggle={toggle} className="hdrTheme iconBtn--sm" />
                    {authPending ? (
                        <span className="skeleton hdrAuthSkeleton" aria-hidden="true" />
                    ) : user ? (
                        <>
                            <CreditsPill user={user} subStatus={subStatus} onClick={openBilling} />
                            <AccountMenu user={user} />
                        </>
                    ) : (
                        <button type="button" className="btn btn--primary btn--sm" onClick={openLogin}>Log in</button>
                    )}
                </div>
            </div>
        </header>
    );
}
